import { z } from 'zod'
import { sponsorIdentityViewSchema, sponsorOpaqueTokenSchema, sponsorOtpChannelSchema } from './sponsor.schema'

// Contrato da API do App do Padrinho — fonte da verdade: este diretorio (PWA-002 Clarification #2).
// Spec PWA-001 — acesso do padrinho com SENHA (telas 02 e 03 do design do TR).
// Decisao do stakeholder (2026-09-25): login por e-mail ou CPF + senha; o codigo de 6 digitos continua
// como verificacao de posse do contato no cadastro e na recuperacao de senha.

// ─── Identificador: e-mail ou CPF ───────────────────────────────

export function isValidCpf(value: string): boolean {
    const d = value.replace(/\D/g, '')
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false
    const digit = (len: number) => {
        let sum = 0
        for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i)
        const rest = (sum * 10) % 11
        return rest === 10 ? 0 : rest
    }
    return digit(9) === Number(d[9]) && digit(10) === Number(d[10])
}

export const sponsorIdentifierSchema = z.string().trim().min(3).max(254).refine(
    v => (v.includes('@') ? z.string().email().safeParse(v).success : isValidCpf(v)),
    'Informe um e-mail ou CPF valido',
)

// ─── Politica de senha ───────────────────────────────
// Minimo 8 caracteres com letra e numero; maximo 128 (hash no servidor com Argon2id/bcrypt).
export const SPONSOR_PASSWORD_MIN = 8

export const sponsorPasswordSchema = z.string()
    .min(SPONSOR_PASSWORD_MIN)
    .max(128)
    .refine(v => /[A-Za-zÀ-ÿ]/.test(v) && /\d/.test(v), 'A senha precisa ter letras e numeros')

// ─── Login (tela 02 · RN-05: 5 erros bloqueiam por 15 min) ───────────────────────────────

export const sponsorLoginSchema = z.object({
    identifier: sponsorIdentifierSchema,
    password: z.string().min(1).max(128),
    // "Manter conectado": sessao longa (30 d ociosa / 180 d absoluta); sem ele, sessao do navegador (12 h)
    remember: z.boolean().default(true),
}).strict()
export type SponsorLoginInput = z.infer<typeof sponsorLoginSchema>

export const sponsorLoginResponseSchema = z.object({
    identity: sponsorIdentityViewSchema,
    token: sponsorOpaqueTokenSchema.optional(),
}).strict()

// ─── Cadastro em 3 etapas (tela 03) ───────────────────────────────
// 1) identifica pelo CPF e envia codigo ao contato ja registrado no CRM
// 2) confere/corrige dados + aceite LGPD e Salvaguarda (RN-06)
// 3) cria a senha

export const sponsorSignupStartSchema = z.object({
    cpf: z.string().refine(isValidCpf, 'CPF invalido'),
    channel: sponsorOtpChannelSchema,
}).strict()
export type SponsorSignupStartInput = z.infer<typeof sponsorSignupStartSchema>

export const sponsorVerificationChallengeSchema = z.object({
    challengeId: z.string().uuid(),
    maskedDestination: z.string().min(1).max(64),
    resendAfterSeconds: z.number().int().positive(),
    expiresInSeconds: z.number().int().positive(),
}).strict()
export type SponsorVerificationChallenge = z.infer<typeof sponsorVerificationChallengeSchema>

export const sponsorVerifyCodeSchema = z.object({
    challengeId: z.string().uuid(),
    code: z.string().regex(/^\d{6}$/),
}).strict()

export const sponsorSignupProfileSchema = z.object({
    fullName: z.string().trim().min(3).max(160),
    mobile: z.string().trim().max(32).nullable(),
    profession: z.string().trim().max(80).nullable(),
    address: z.string().trim().max(200).nullable(),
}).strict()
export type SponsorSignupProfile = z.infer<typeof sponsorSignupProfileSchema>

// Resposta da etapa 1→2: token da inscricao + dados encontrados no CRM (CPF so mascarado, somente leitura)
export const sponsorSignupVerifiedSchema = z.object({
    signupToken: sponsorOpaqueTokenSchema,
    cpfMasked: z.string().min(1).max(20),
    profile: sponsorSignupProfileSchema,
}).strict()
export type SponsorSignupVerified = z.infer<typeof sponsorSignupVerifiedSchema>

export const sponsorSignupCompleteSchema = z.object({
    signupToken: sponsorOpaqueTokenSchema,
    profile: sponsorSignupProfileSchema,
    // RN-06: sem os dois aceites nao ha conta
    consents: z.object({ privacy: z.literal(true), safeguarding: z.literal(true) }).strict(),
    password: sponsorPasswordSchema,
}).strict()
export type SponsorSignupCompleteInput = z.infer<typeof sponsorSignupCompleteSchema>

// ─── Recuperacao de senha ───────────────────────────────

export const sponsorPasswordForgotSchema = z.object({
    identifier: sponsorIdentifierSchema,
    channel: sponsorOtpChannelSchema,
}).strict()
export type SponsorPasswordForgotInput = z.infer<typeof sponsorPasswordForgotSchema>

export const sponsorPasswordResetTokenSchema = z.object({ resetToken: sponsorOpaqueTokenSchema }).strict()

export const sponsorPasswordResetSchema = z.object({
    resetToken: sponsorOpaqueTokenSchema,
    password: sponsorPasswordSchema,
}).strict()
export type SponsorPasswordResetInput = z.infer<typeof sponsorPasswordResetSchema>

/** PWA-031 D-06: alterar a senha com sessao, informando a atual. */
export const sponsorPasswordChangeSchema = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: sponsorPasswordSchema,
}).strict()
export type SponsorPasswordChangeInput = z.infer<typeof sponsorPasswordChangeSchema>

// ─── Spec PWA-007: trocar e-mail ou celular (codigo no contato NOVO) ───────────────────────────────

/** Celular brasileiro: 10 ou 11 digitos (DDD + numero), com ou sem +55. */
export function isValidBrazilianMobile(value: string): boolean {
    const d = value.replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '')
    return /^[1-9]{2}9?\d{8}$/.test(d)
}

export const sponsorContactChangeStartSchema = z.discriminatedUnion('field', [
    z.object({ field: z.literal('email'), value: z.string().trim().toLowerCase().email().max(254) }).strict(),
    z.object({ field: z.literal('mobile'), value: z.string().trim().max(32).refine(isValidBrazilianMobile, 'Celular invalido') }).strict(),
])
export type SponsorContactChangeStartInput = z.infer<typeof sponsorContactChangeStartSchema>

export const sponsorContactChangeVerifySchema = z.object({
    challengeId: z.string().uuid(),
    code: z.string().regex(/^\d{6}$/),
}).strict()
